import React from 'react';
import { useCustomPermissions } from '../hooks/usePermissions';

interface PermissionGuardProps {
  resource: string;
  action: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Show children only if user has permission
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  resource,
  action,
  children,
  fallback = null,
}) => {
  const { hasPermission } = useCustomPermissions();

  if (hasPermission(resource, action)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

interface AnyPermissionGuardProps {
  permissions: Array<{ resource: string; action: string }>;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Show children if user has ANY of the permissions
 */
export const AnyPermissionGuard: React.FC<AnyPermissionGuardProps> = ({
  permissions,
  children,
  fallback = null,
}) => {
  const { hasAnyPermission } = useCustomPermissions();

  if (hasAnyPermission(permissions)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

interface RoleGuardProps {
  roles: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Show children only if user has one of the specified roles
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  roles,
  children,
  fallback = null,
}) => {
  const { hasRole, isSuperAdmin } = useCustomPermissions();

  // Super admin can see everything
  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  if (roles.some((role) => hasRole(role))) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

interface SuperAdminGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Show children only for super admin
 */
export const SuperAdminGuard: React.FC<SuperAdminGuardProps> = ({
  children,
  fallback = null,
}) => {
  const { isSuperAdmin } = useCustomPermissions();

  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};